/* eslint-disable react/prop-types */
// AVI Forms — public, no-auth submission page.
// Renders dynamically from GET /api/public/forms/{form_id} and POSTs the
// answers as multipart/form-data to /submit.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Send,
  FileText,
  Camera,
  ImagePlus,
  X as XIcon,
} from "lucide-react";

const onlyDigits = (s) => (s || "").replace(/\D+/g, "");

const maskCPF = (s) => {
  const d = onlyDigits(s).slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};
const maskPhone = (s) => {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
};
const maskCEP = (s) => {
  const d = onlyDigits(s).slice(0, 8);
  return d.replace(/(\d{5})(\d{1,3})$/, "$1-$2");
};

const Field = ({ field, value, files, pendingMeta, onChange, onFile }) => {
  const t = (field.type || "text").toLowerCase();
  const id = `field-${field.id}`;
  const labelEl = (
    <Label htmlFor={id} className="text-sm font-medium text-slate-800 mb-1.5 block">
      {field.label}
      {field.required && <span className="text-rose-600 ml-1">*</span>}
    </Label>
  );
  // Mobile-first: taller tap targets, larger fonts to avoid iOS zoom (>=16px).
  const inputCls = "h-12 text-base sm:h-10 sm:text-sm";
  const selectCls =
    "w-full h-12 sm:h-10 border border-slate-300 rounded-md px-3 text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500";

  if (t === "textarea") {
    return (
      <div>
        {labelEl}
        <Textarea
          id={id}
          value={value || ""}
          rows={3}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
          className="text-base sm:text-sm"
          data-testid={`public-form-field-${field.id}`}
        />
      </div>
    );
  }
  if (t === "select") {
    return (
      <div>
        {labelEl}
        <select
          id={id}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
          className={selectCls}
          data-testid={`public-form-field-${field.id}`}
        >
          <option value="">Selecione…</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (t === "radio" || t === "yesno") {
    const opts = t === "yesno" ? ["SIM", "NÃO"] : field.options || [];
    return (
      <div>
        {labelEl}
        <div className="flex flex-wrap gap-2">
          {opts.map((o) => (
            <label
              key={o}
              className={`px-4 py-2.5 sm:py-1.5 text-sm sm:text-xs rounded-full border cursor-pointer transition active:scale-95 ${
                value === o
                  ? "border-indigo-600 bg-indigo-50 text-indigo-700 font-semibold"
                  : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              <input
                type="radio"
                name={field.id}
                value={o}
                checked={value === o}
                onChange={() => onChange(field.id, o)}
                className="hidden"
              />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (t === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) =>
      onChange(
        field.id,
        arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o],
      );
    return (
      <div>
        {labelEl}
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((o) => (
            <label
              key={o}
              className={`px-3 py-2 sm:py-1.5 text-sm sm:text-xs rounded-md border cursor-pointer transition active:scale-95 ${
                arr.includes(o)
                  ? "border-indigo-600 bg-indigo-50 text-indigo-700 font-semibold"
                  : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              <input
                type="checkbox"
                checked={arr.includes(o)}
                onChange={() => toggle(o)}
                className="mr-1.5"
              />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (t === "date") {
    return (
      <div>
        {labelEl}
        <Input
          id={id}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
          className={inputCls}
          data-testid={`public-form-field-${field.id}`}
        />
      </div>
    );
  }
  if (t === "file") {
    const f = files[field.id];
    const isImage = f && (f.type || "").startsWith("image/");
    const pending = !f && pendingMeta && pendingMeta[field.id];
    return (
      <div>
        {labelEl}
        {f ? (
          <div className="border-2 border-emerald-500 bg-emerald-50 rounded-lg p-3 flex items-center gap-3">
            {isImage ? (
              <img
                src={URL.createObjectURL(f)}
                alt="prévia"
                className="w-14 h-14 rounded-md object-cover border border-emerald-300"
              />
            ) : (
              <div className="w-14 h-14 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-emerald-700" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{f.name}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {(f.size / 1024).toFixed(0)} KB
              </p>
            </div>
            <button
              type="button"
              onClick={() => onFile(field.id, null)}
              className="p-2 rounded-md bg-white border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-300 active:scale-95"
              aria-label="Remover arquivo"
              data-testid={`public-form-remove-${field.id}`}
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            {pending && (
              <div className="mb-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                ⚠️ Você já tinha anexado <strong>{pending.name}</strong> antes
                do recarregamento. Por favor, anexe novamente.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label
                htmlFor={`${id}-cam`}
                className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg px-2 py-4 cursor-pointer active:bg-indigo-100 min-h-[96px] ${
                  pending
                    ? "border-amber-400 bg-amber-50/60"
                    : "border-indigo-300 bg-indigo-50/40 hover:border-indigo-500"
                }`}
              >
                <Camera className="w-6 h-6 text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-700">
                  Tirar foto
                </span>
                <span className="text-[10px] text-slate-500 leading-tight text-center">
                  usa a câmera
                </span>
                <input
                  id={`${id}-cam`}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => onFile(field.id, e.target.files?.[0] || null)}
                  data-testid={`public-form-camera-${field.id}`}
                />
              </label>
              <label
                htmlFor={id}
                className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg px-2 py-4 cursor-pointer active:bg-slate-100 min-h-[96px] ${
                  pending
                    ? "border-amber-400 bg-amber-50/60"
                    : "border-slate-300 bg-white hover:border-slate-400"
                }`}
              >
                <ImagePlus className="w-6 h-6 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700">
                  Anexar arquivo
                </span>
                <span className="text-[10px] text-slate-400 leading-tight text-center">
                  PDF, JPG ou PNG · 12 MB
                </span>
                <input
                  id={id}
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={(e) => onFile(field.id, e.target.files?.[0] || null)}
                  data-testid={`public-form-field-${field.id}`}
                />
              </label>
            </div>
          </>
        )}
      </div>
    );
  }

  // text-like
  const maskers = {
    cpf: maskCPF,
    phone: maskPhone,
    cep: maskCEP,
    pis: (s) => onlyDigits(s).slice(0, 11),
    rg: (s) => onlyDigits(s).slice(0, 14),
  };
  const onChangeText = (e) => {
    const v = maskers[t] ? maskers[t](e.target.value) : e.target.value;
    onChange(field.id, v);
  };
  const inputMode =
    t === "email"
      ? "email"
      : ["phone", "cpf", "cep", "pis", "rg"].includes(t)
        ? "numeric"
        : "text";
  return (
    <div>
      {labelEl}
      <Input
        id={id}
        type={t === "email" ? "email" : "text"}
        inputMode={inputMode}
        autoComplete={t === "email" ? "email" : "off"}
        value={value || ""}
        placeholder={field.placeholder || ""}
        onChange={onChangeText}
        className={inputCls}
        data-testid={`public-form-field-${field.id}`}
      />
    </div>
  );
};

const PublicForm = () => {
  const { formId } = useParams();
  const draftKey = `avi-form-draft:${formId}`;
  const filesMetaKey = `avi-form-files:${formId}`;
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [pendingFileMeta, setPendingFileMeta] = useState({}); // files lost on reload
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // ─── Draft restore: protects users on flaky mobile (iOS kills the page
  // when the camera consumes too much memory and reloads on return). We
  // persist text answers continuously so the user never loses what they
  // typed; files cannot be persisted across reload but we keep their
  // metadata so the user is reminded which attachments to re-upload.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/forms/${formId}`);
        setForm(r.data);
        // Restore answers AFTER the form is loaded so labels match.
        let restoredAnswers = false;
        try {
          const raw = localStorage.getItem(draftKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
              setAnswers(parsed);
              restoredAnswers = Object.keys(parsed).length > 0;
            }
          }
        } catch {
          /* ignore */
        }
        try {
          const raw = localStorage.getItem(filesMetaKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
              setPendingFileMeta(parsed);
            }
          }
        } catch {
          /* ignore */
        }
        if (restoredAnswers) {
          toast.info("Recuperamos o que você já tinha digitado.", {
            duration: 4000,
          });
        }
      } catch (e) {
        setError(
          e?.response?.data?.detail || "Não foi possível carregar o formulário.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [formId, draftKey, filesMetaKey]);

  // Persist text answers on every keystroke (debounced minimally via React batching)
  useEffect(() => {
    if (!form) return;
    try {
      if (Object.keys(answers).length === 0) {
        localStorage.removeItem(draftKey);
      } else {
        localStorage.setItem(draftKey, JSON.stringify(answers));
      }
    } catch {
      /* quota or private mode — ignore */
    }
  }, [answers, form, draftKey]);

  // Track which files have already been picked (only their filename + size
  // metadata, since Blobs can't be safely persisted in localStorage).
  useEffect(() => {
    if (!form) return;
    try {
      const meta = {};
      Object.entries(files).forEach(([fid, f]) => {
        meta[fid] = { name: f.name, size: f.size, type: f.type };
      });
      if (Object.keys(meta).length === 0) {
        localStorage.removeItem(filesMetaKey);
      } else {
        localStorage.setItem(filesMetaKey, JSON.stringify(meta));
      }
    } catch {
      /* ignore */
    }
  }, [files, form, filesMetaKey]);

  const onChange = (id, value) =>
    setAnswers((a) => ({ ...a, [id]: value }));
  // Compress images that come out of the phone camera (often 4-10MB JPEG)
  // before storing them in state. Keeps PDFs untouched. Targets ~1600px max
  // edge and ~0.82 JPEG quality — typical doc photo lands ~300-800KB.
  const compressIfImage = async (file) => {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    if (file.size < 800 * 1024) return file; // small enough, skip
    try {
      const bmp = await createImageBitmap(file);
      const maxEdge = 1600;
      const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
      const w = Math.round(bmp.width * scale);
      const h = Math.round(bmp.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bmp, 0, 0, w, h);
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.82),
      );
      if (!blob || blob.size >= file.size) return file;
      const safeName = (file.name || "foto.jpg").replace(/\.[^.]+$/, "") + ".jpg";
      return new File([blob], safeName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch {
      return file; // any failure → keep original
    }
  };

  const onFile = async (id, file) => {
    if (!file) {
      setFiles((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
      setPendingFileMeta((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
      return;
    }
    const optimized = await compressIfImage(file);
    if (optimized.size > 12 * 1024 * 1024) {
      toast.error(`"${optimized.name}" excede 12 MB.`);
      return;
    }
    setFiles((s) => ({ ...s, [id]: optimized }));
    // The new file overrides any "pending re-upload" warning.
    setPendingFileMeta((s) => {
      if (!s[id]) return s;
      const next = { ...s };
      delete next[id];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Client-side validation
    for (const f of form.fields) {
      const t = (f.type || "text").toLowerCase();
      const required = !!f.required;
      if (t === "file") {
        if (required && !files[f.id]) {
          toast.error(`Anexe "${f.label}".`);
          return;
        }
        continue;
      }
      const v = answers[f.id];
      const isEmpty =
        v === undefined ||
        v === null ||
        v === "" ||
        (Array.isArray(v) && v.length === 0);
      if (required && isEmpty) {
        toast.error(`Preencha "${f.label}".`);
        return;
      }
      if (t === "cpf" && v && onlyDigits(v).length !== 11) {
        toast.error(`CPF inválido em "${f.label}".`);
        return;
      }
      if (t === "email" && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        toast.error(`E-mail inválido em "${f.label}".`);
        return;
      }
    }

    const fd = new FormData();
    for (const f of form.fields) {
      const t = (f.type || "text").toLowerCase();
      if (t === "file") {
        if (files[f.id]) fd.append(f.id, files[f.id]);
        continue;
      }
      const v = answers[f.id];
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        v.forEach((x) => fd.append(f.id, x));
      } else {
        fd.append(f.id, String(v));
      }
    }

    // Total size safety check (backend limit is 14 MB).
    const totalBytes = Object.values(files).reduce(
      (s, f) => s + (f?.size || 0), 0,
    );
    if (totalBytes > 14 * 1024 * 1024) {
      toast.error(
        `Tamanho total dos anexos (${(totalBytes / 1024 / 1024).toFixed(1)} MB) excede o limite de 14 MB.`,
      );
      return;
    }

    setSubmitting(true);
    const startedAt = Date.now();
    try {
      const r = await axios.post(
        `${API}/public/forms/${formId}/submit`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        },
      );
      // Clear local draft — submission persisted on the server.
      try {
        localStorage.removeItem(draftKey);
        localStorage.removeItem(filesMetaKey);
      } catch {
        /* ignore */
      }
      setSubmitted(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const elapsedS = Math.round((Date.now() - startedAt) / 1000);
      const sizeMB = (totalBytes / 1024 / 1024).toFixed(2);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (detail) {
        msg = detail;
      } else if (status === 413) {
        msg = `Anexos muito grandes (${sizeMB} MB). Reduza os PDFs e tente novamente.`;
      } else if (status === 422) {
        msg = "Algum campo obrigatório está inválido. Revise o formulário.";
      } else if (status >= 500) {
        msg = `Erro no servidor (${status}). Tente novamente em alguns instantes.`;
      } else if (err?.code === "ECONNABORTED" || elapsedS >= 85) {
        msg = `Tempo esgotado após ${elapsedS}s. Verifique sua conexão e tente novamente.`;
      } else if (!err?.response) {
        msg = `Sem resposta do servidor após ${elapsedS}s (${sizeMB} MB). Verifique sua conexão.`;
      } else {
        msg = `Falha ao enviar (${status || "sem status"}) após ${elapsedS}s. Tente novamente.`;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="public-form-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="public-form-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">
            Formulário indisponível
          </h2>
          <p className="text-sm text-slate-600">{error || "Não encontrado."}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm"
          data-testid="public-form-success"
        >
          <img
            src="/logo_full.png"
            alt="AVI Segurança"
            className="h-12 mx-auto object-contain mb-4"
          />
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-1">
            Resposta enviada!
          </h2>
          <p className="text-sm text-slate-600 mb-3">
            Recebemos seus dados. O RH da AVI Segurança entrará em contato em breve.
          </p>
          <p className="text-[11px] text-slate-400 font-mono">
            Protocolo #{(submitted.submission_id || "").slice(0, 8).toUpperCase()}
          </p>
        </motion.div>
      </div>
    );
  }

  const ordered = [...(form.fields || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  // Progress: count completed required fields (or filled file fields).
  const totalRequired = ordered.filter((f) => f.required).length || 1;
  const completedRequired = ordered.filter((f) => {
    if (!f.required) return false;
    if ((f.type || "").toLowerCase() === "file") return !!files[f.id];
    const v = answers[f.id];
    if (v === undefined || v === null || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  }).length;
  const progressPct = Math.round((completedRequired / totalRequired) * 100);

  return (
    <div className="min-h-screen bg-slate-50 px-0 sm:py-6 sm:px-6 pb-28 sm:pb-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-200 to-slate-100 text-slate-900 sm:rounded-t-2xl px-4 py-4 sm:px-7 sm:py-6 shadow-sm sticky top-0 z-20 border-b border-slate-300">
          <div className="flex items-center gap-3">
            <img
              src="/logo_full.png"
              alt="AVI Segurança"
              className="h-12 sm:h-14 w-auto object-contain flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">
                <FileText className="w-3.5 h-3.5" />
                Formulário oficial
              </div>
              <h1 className="text-lg sm:text-2xl font-semibold leading-snug truncate text-slate-900">
                {form.title}
              </h1>
            </div>
          </div>
          {form.description && (
            <p className="text-xs sm:text-sm text-slate-600 mt-2">
              {form.description}
            </p>
          )}
          {/* Progress bar */}
          <div className="mt-3 h-1.5 rounded-full bg-slate-300/70 overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-[10px] text-slate-500 mt-1.5">
            {completedRequired} de {totalRequired} campos obrigatórios preenchidos
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border-x border-b border-slate-200 sm:rounded-b-2xl px-4 py-5 sm:p-7 space-y-5 shadow-sm"
          data-testid="public-form"
        >
          <p className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-md p-2.5 leading-relaxed">
            <strong>Atenção:</strong> os dados serão usados apenas para o seu
            cadastro de admissão na AVI Segurança. Em campos de documento, você
            pode <strong>tirar foto com o celular</strong> caso não tenha o PDF
            salvo.
          </p>
          {ordered.map((f) => (
            <Field
              key={f.id}
              field={f}
              value={answers[f.id]}
              files={files}
              pendingMeta={pendingFileMeta}
              onChange={onChange}
              onFile={onFile}
            />
          ))}

          {/* Inline submit (desktop/visible flow). Mobile also has a sticky one below. */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 sm:h-10 text-base sm:text-sm bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="public-form-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar
              </>
            )}
          </Button>
          <p className="text-[10px] text-center text-slate-400">
            Formulários AVI · Plataforma interna · Não compartilhe este link
            fora da empresa.
          </p>
        </form>
      </div>

      {/* Sticky submit footer — mobile only */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 z-30 shadow-lg">
        <Button
          type="button"
          onClick={(e) => submit(e)}
          disabled={submitting}
          className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="public-form-submit-sticky"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enviando…
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Enviar formulário
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PublicForm;
